import clsx from 'clsx';
import React from 'react';

import styles from './TargetPriceChart.module.scss';
import {
  type TargetPriceChartProps,
  useChartData,
} from './use-chart-data/useChartData';

/**
 * Composite chart pairing historical price (left half, line plot)
 * with analyst target prices (right half, horizontal levels for high
 * / average / low). The line color is bullish or bearish based on
 * whether the latest price is above or below the average target.
 *
 * Renders an "No data available" message when `timeseries` is empty.
 */
export const TargetPriceChart = ({
  timeseries,
  targetPrice,
}: TargetPriceChartProps) => {
  const dimensions = {
    width: 440,
    height: 215,
    margin: { top: 5, right: 50, bottom: 40, left: 0 },
  };

  const chartWidth =
    dimensions.width - dimensions.margin.left - dimensions.margin.right;
  const chartHeight =
    dimensions.height - dimensions.margin.top - dimensions.margin.bottom;
  const leftChartWidth = chartWidth / 2;
  const rightChartWidth = chartWidth / 2;

  const { scales, paths, axisData, isBullish, targetBearish } = useChartData({
    timeseries,
    targetPrice,
    leftChartWidth,
    rightChartWidth,
    chartHeight,
  });

  if (!scales || !paths || !axisData) {
    return (
      <svg
        className={styles.chartSvg}
        width={dimensions.width}
        height={dimensions.height}
      >
        <text
          className={styles.axisText}
          x={dimensions.width / 2}
          y={dimensions.height / 2}
          textAnchor="middle"
        >
          No data available
        </text>
      </svg>
    );
  }

  return (
    <svg
      className={styles.chartSvg}
      width={dimensions.width}
      height={dimensions.height}
    >
      <g
        transform={`translate(${dimensions.margin.left}, ${dimensions.margin.top})`}
      >
        {/* Time series line chart (left side) */}
        <path
          className={clsx(styles.timeSeriesLine, {
            [styles.timeSeriesLineBullish]: isBullish,
          })}
          d={paths.timeseries}
        />

        {/* Vertical divider line */}
        <line
          className={styles.dividerLine}
          x1={leftChartWidth}
          y1={0}
          x2={leftChartWidth}
          y2={chartHeight}
        />

        {/* Target price lines (right side) */}
        <path
          className={clsx(styles.targetLine, {
            [styles.targetLineBearish]: targetBearish?.high || false,
          })}
          d={paths.targetHigh}
        />
        <path
          className={clsx(styles.targetLine, styles.targetLineAverage, {
            [styles.targetLineBearish]: targetBearish?.average || false,
          })}
          d={paths.targetAvg}
        />
        <path
          className={clsx(styles.targetLine, {
            [styles.targetLineBearish]: targetBearish?.low || false,
          })}
          d={paths.targetLow}
        />

        {/* X-axis line */}
        <line
          className={styles.xAxisLine}
          x1={0}
          y1={chartHeight}
          x2={chartWidth}
          y2={chartHeight}
        />

        {/* Y-axis labels */}
        {axisData.yAxis.map((tick, i) => (
          <g key={i}>
            <line
              className={styles.axisLine}
              x1={0}
              y1={tick.y}
              x2={chartWidth}
              y2={tick.y}
            />
            <text
              className={styles.axisText}
              x={chartWidth + 15}
              y={tick.y}
              dy="0.35em"
              textAnchor="start"
            >
              {tick.label}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {axisData.xAxis.map((tick, i) => {
          // Determine text anchor based on position
          let textAnchor: 'start' | 'middle' | 'end' = 'middle';

          if (i === 0) {
            textAnchor = 'start'; // First label: left-aligned
          }

          if (i === 2) {
            textAnchor = 'end'; // Last label: right-aligned
          }

          return (
            <text
              key={i}
              className={styles.xAxisText}
              x={tick.x}
              y={chartHeight + 25}
              textAnchor={textAnchor}
            >
              {tick.label}
            </text>
          );
        })}
      </g>
    </svg>
  );
};
